import { useProject, useWidget } from '@haohan/clw-store'
import { cloneDeep, has } from 'lodash-es'
import { getKey, getCompChildren, hasChildrenFn, emitter } from '@haohan/clw-utils'
import { EMITTER_NAME } from '../common/emitter-constant'
import { useArea } from './useArea'
import { ref, computed } from 'vue'

const { areaInfo, judgeAreaFn, alignFn, rangeFn } = useArea()

// 左击
const activeWidgets = ref<{ [key: string]: Widget }>({})
const activeWidgetKeys = computed(() => Object.keys(activeWidgets.value))
const activeWidgetslist = computed(() => Object.values(activeWidgets.value))
const shiftInfo = ref<{ start: number; end: number }>({
  start: -1,
  end: -1
})

// 右击
const showOrder = ref(false)
const orderPos = ref({ x: 0, y: 0 })
const rightSelectWidget = ref<Widget | null>(null)
const copyWidget = ref<Widget | null>(null)
const BASE_ORDER_LIST = {
  copy: { label: '复制' },
  paste: { label: '粘贴', disable: false },
  rename: { label: '重命名' },
  remove: { label: '删除', disable: false }
}
const orderList = ref(cloneDeep(BASE_ORDER_LIST))

function upChildKey(widgets: Widget[]) {
  for (let ix = 0, len = widgets.length; ix < len; ix++) {
    const widget = widgets[ix]
    widget.key = getKey()
    widget.events = []
    const children = getCompChildren(widget)
    if (children.length) {
      for (const childObj of children) {
        upChildKey(childObj.list)
      }
    }
  }
}

const maxZIndex = ref(0)
const minZIndex = ref(0)
const shouldSort = ref(false)

export function useCompLayer() {
  const widgetStore = useWidget()
  const projectStore = useProject()

  const isBigScreenPage = computed(() => projectStore.isBigScreenPage)
  /**
   * 切换组件选中状态
   * @param el 当前组件
   * @param status 状态
   */
  function changeWidgetSelected(el: Widget, status = true) {
    if (has(el, 'selected')) {
      el.selected = status
    }
  }
  /**
   * 仅点击某个图层时，将其选中并作为当前选择的图层，
   * 若存在多个激活图层，则会只保留当前点击的图层
   */
  function selectedWidgetFn(el: Widget, record = true) {
    if (isBigScreenPage.value) {
      activeWidgets.value = {
        [el.key]: el
      }
      emitter.emit(EMITTER_NAME.EditLayerChange, { type: 'selectChange', widget: el })
    }
    widgetStore.changeComposeUsable(false)
    widgetStore.changeSelectWidget(el)
    emitter.emit(EMITTER_NAME.ShowComponentAttribute)
  }

  /**
   * 按住Ctrl键点击某个图层时，将其置为激活的图层，
   * 若该图层已激活，则会取消激活
   */
  function multiSelectFn(el: Widget, record = true) {
    if (!record) {
      return
    }
    if (activeWidgets.value[el.key]) {
      delete activeWidgets.value[el.key]
    } else {
      activeWidgets.value[el.key] = el
    }
    if (activeWidgetslist.value.length > 1) {
      widgetStore.changeComposeUsable(true)
      judgeAreaFn(activeWidgetslist.value)
    } else {
      widgetStore.changeComposeUsable(false)
    }
    emitter.emit(EMITTER_NAME.EditLayerChange, { type: 'selectChange', widget: el })
  }
  /**
   *
   * @param list
   * @param sendEmit 是否发送更新事件，避免大屏编辑页死循环
   */
  function multiSelectFnV2(list: Widget[], sendEmit = true) {
    for (const item of list) {
      activeWidgets.value[item.key] = item
    }
    widgetStore.changeComposeUsable(true)
    judgeAreaFn(activeWidgetslist.value)
    if (sendEmit) {
      emitter.emit(EMITTER_NAME.EditLayerChange, { type: 'selectChange', widget: el })
    }
  }
  function resetActiveWidgetsFn() {
    activeWidgets.value = {}
    resetShiftInfo()
    orderList.value = cloneDeep(BASE_ORDER_LIST)
  }
  function rmWidgetFn(el: Widget) {
    // 如果是大屏，并且组件处于锁定状态，则不能删除
    if (isBigScreenPage.value && el.isLock) {
      return false
    }
    widgetStore.deleteSelectWidgetFromWidgetDataList(el)
    emitter.emit(EMITTER_NAME.RemoveWidget, el)
    emitter.emit(EMITTER_NAME.EditLayerChange, { type: 'selectChange', widget: el })
  }

  // 右击
  function changeRightListFn(ev: MouseEvent, el: Widget) {
    // console.log('changeRightListFn', el)
    rightSelectWidget.value = el
    if (!copyWidget.value || !hasChildrenFn(el)) {
      orderList.value.paste.disable = true
    } else {
      orderList.value.paste.disable = false
    }
    // 增加大屏页特殊处理
    if (projectStore.isBigScreenPage) {
      orderList.value['lock'] = { label: `${el.isLock ? '解锁' : '锁定'}` }
      orderList.value['remove'].disable = el.isLock ? true : false
      orderList.value['hidden'] = { label: `${el.hidden ? '显示' : '隐藏'}` }
    }
    orderPos.value = { x: ev.pageX, y: ev.pageY }
    showOrder.value = true
  }
  function hideRightListFn() {
    showOrder.value = false
    orderPos.value = { x: 0, y: 0 }
    rightSelectWidget.value = null
  }
  function orderCopyFn(el: Widget) {
    copyWidget.value = el
  }
  function orderPasteFn(el: Widget) {
    const newWidget = cloneDeep(el)
    newWidget.key = getKey()
    newWidget.events = []
    if (newWidget.type === 'Group' && newWidget.groupComponents?.length) {
      newWidget.groupComponents?.forEach((item: Widget) => {
        item.key = getKey()
        item.events = []
      })
    } else if (newWidget.type !== 'Group') {
      const children = getCompChildren(newWidget)
      if (children.length) {
        upChildKey(children)
      }
    }
    const { type } = rightSelectWidget.value
    if (type === 'Group' && rightSelectWidget.value?.groupComponents?.length) {
      rightSelectWidget.value.groupComponents?.push(newWidget)
    } else if (type !== 'Group') {
      const rightSelectWidgetChildren = getCompChildren(rightSelectWidget.value)
      if (rightSelectWidgetChildren.length) {
        rightSelectWidgetChildren[0].list.push(newWidget)
      }
    }
  }

  const changeWidgetLockStatue = (el: Widget) => {
    emitter.emit(EMITTER_NAME.EditLayerChange, {
      type: 'lockChange',
      data: el
    })
  }
  const changeWidgetHiddenStatue = (el: Widget) => {
    emitter.emit(EMITTER_NAME.EditLayerChange, {
      type: 'hiddenChange',
      data: el
    })
  }
  function orderFn(type: string) {
    // console.log('orderFn', type)
    switch (type) {
      case 'copy':
        orderCopyFn(rightSelectWidget.value)
        break
      case 'paste':
        orderPasteFn(copyWidget.value)
        break
      case 'rename':
        rightSelectWidget.value!.editable = true
        break
      case 'remove':
        rmWidgetFn(rightSelectWidget.value)
        break
      case 'lock':
        changeWidgetLockStatue(rightSelectWidget.value)
        break
      case 'hidden':
        changeWidgetHiddenStatue(rightSelectWidget.value)
        break
      default:
        break
    }
    showOrder.value = false
  }

  const nestAlignFn = (type: string) => {
    alignFn(type, activeWidgetslist.value)
  }
  const nestRangeFn = (type: string) => {
    rangeFn(type, activeWidgetslist.value)
  }

  function setStart(ix: number) {
    shiftInfo.value.start = ix
  }
  function setEnd(ix: number, list: Widget[]) {
    shiftInfo.value.end = ix
    activeWidgets.value = {}
    for (const item of list) {
      activeWidgets.value[item.key] = item
    }
    if (activeWidgetslist.value.length > 1) {
      widgetStore.changeComposeUsable(true)
      judgeAreaFn(activeWidgetslist.value)
    } else {
      widgetStore.changeComposeUsable(false)
    }
    emitter.emit(EMITTER_NAME.EditLayerChange)
  }
  function resetShiftInfo() {
    shiftInfo.value = { start: -1, end: -1 }
  }

  function setMaxZIndex(zIndex: number) {
    maxZIndex.value = zIndex
  }
  function setMinZIndex(zIndex: number) {
    minZIndex.value = zIndex
  }
  function setShouldSort(flag: boolean) {
    shouldSort.value = flag
  }

  return {
    activeWidgets,
    activeWidgetKeys,
    activeWidgetslist,
    selectedWidgetFn,
    multiSelectFn,
    rmWidgetFn,
    orderList,
    showOrder,
    orderPos,
    changeRightListFn,
    hideRightListFn,
    orderFn,
    resetActiveWidgetsFn,
    areaInfo,
    nestAlignFn,
    nestRangeFn,
    setStart,
    setEnd,
    resetShiftInfo,
    shiftInfo,
    maxZIndex,
    minZIndex,
    setMaxZIndex,
    setMinZIndex,
    shouldSort,
    setShouldSort,
    multiSelectFnV2,
    changeWidgetLockStatue,
    changeWidgetHiddenStatue,
    isBigScreenPage
  }
}
